import { FC, useCallback, useMemo } from "react";

import { useFullUserProfileContext, UserEntityModalType } from "Bands/contexts";
import { copy } from "shared/assets";
import { Marker, Plus } from "shared/assets/icons";
import { Button, Card, Loader, Message, PlaceTeaser } from "shared/components";
import { Spacer } from "shared/layout";
import { GridContainer, Icon } from "shared/styles";
import { ITheme } from "shared/types";
import { useFetchingContactDetails } from "Bands/utils";

const addressCopy = copy.userAddress;

export const Addresses: FC = () => {
  const { addresses, setShowCreateModal, setShowEditModal, setShowDeleteModal, getItemActions } =
    useFullUserProfileContext();

  const getActions = useCallback(
    (id: string) =>
      getItemActions({
        handleEdit: () =>
          setShowEditModal({
            id: id,
            type: UserEntityModalType.address,
          }),
        handleDelete: () =>
          setShowDeleteModal({
            id: id,
            type: UserEntityModalType.address,
          }),
      }),
    [getItemActions, setShowDeleteModal, setShowEditModal]
  );

  const { fetchingAddresses } = useFetchingContactDetails();

  const content = useMemo(() => {
    if (fetchingAddresses) {
      return <Loader />;
    }

    if (!addresses?.length) {
      return <Message message={addressCopy.messages.empty} />;
    }

    return (
      <GridContainer>
        {addresses?.map(address => (
          <PlaceTeaser key={address.id} {...address} actions={getActions(address.id)} />
        ))}
      </GridContainer>
    );
  }, [addresses, fetchingAddresses, getActions]);

  return (
    <Card icon={Marker} title={addressCopy.title}>
      <Spacer size="s">
        {content}
        <Button
          onClick={() => setShowCreateModal(UserEntityModalType.address)}
          data-testid="add_address_button"
          theme={ITheme.secondary}
        >
          <Icon as={Plus} />
          <span>{addressCopy.buttons.create}</span>
        </Button>
      </Spacer>
    </Card>
  );
};
